import { useMemo } from 'react';

import { useQuery } from '@apollo/client';
import type { WatchQueryFetchPolicy } from '@apollo/client';

import { useEnterpriseAdminPermissions } from 'bundles/enterprise-admin-roles/components/EnterprisePermissionsProvider';

import EnterpriseProgramsByOrganizationSlug from '../queries/EnterpriseProgramsByOrganizationSlug.graphql';
import type {
  EnterpriseProgramsByOrganizationSlugQuery,
  EnterpriseProgramsByOrganizationSlugQueryVariables,
} from '../queries/__generated__/EnterpriseProgramsByOrganizationSlug';

export const useEnterpriseProgramsByOrganizationSlug = (
  // Accept a missing slug so callers can render before the org context resolves without an
  // extra `?? ''`. The query is skipped when it is falsy (see `skip` below); firing it with a
  // null/undefined `organizationSlug` (a required `String!`) makes the gateway reject the whole
  // request with HTTP 400. See Sentry ADMIN-DASHBOARD-292.
  organizationSlug: string | null | undefined,
  subdomain?: 'LEARNERS',
  includeArchived?: boolean,
  limit?: number,
  // Optional override; defaults to Apollo's cache-first. Callers that need fresh data on mount
  // (e.g. the Groups listing) can opt into 'cache-and-network'.
  fetchPolicy?: WatchQueryFetchPolicy,
  // When true, skip the VIEW-permission filter below and return every org program regardless of
  // the caller's role. Defaults to false so existing callers keep the permission-scoped list.
  includeAllPrograms?: boolean
) => {
  const { data, loading, error, refetch } = useQuery<
    EnterpriseProgramsByOrganizationSlugQuery,
    EnterpriseProgramsByOrganizationSlugQueryVariables
  >(EnterpriseProgramsByOrganizationSlug, {
    variables: { organizationSlug: organizationSlug ?? '', includeArchived, limit },
    context: { clientName: 'gatewayGql' },
    fetchPolicy,
    skip: !organizationSlug,
  });
  const { hasPermission, getIsOrganizationDomainAdmin } = useEnterpriseAdminPermissions();
  const isOrgAdmin = getIsOrganizationDomainAdmin();
  const programs = useMemo(
    () =>
      data?.EnterpriseProgram?.queryByOrganizationSlug.elements
        .filter(
          (program) =>
            includeAllPrograms ||
            isOrgAdmin ||
            hasPermission({
              domain: 'PROGRAM',
              domainId: program.id,
              verb: 'VIEW',
              subdomain,
            })
        )
        .map((_) => ({ id: _.id, name: _.name, slug: _.slug, endedAt: _.endedAt })),
    [data?.EnterpriseProgram, hasPermission, subdomain, isOrgAdmin, includeAllPrograms]
  );
  return { programs, loading, error, refetch };
};
